import { useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { useAuth } from "@/lib/auth";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { useGruposVisiveis } from "../use-grupos-visiveis";
import { abaAtiva, abasDoCelular, modoDoCelular, type AbaDoCelular } from "./abas-do-celular";
import { MenuCriar } from "./MenuCriar";
import { useAcoesDeCriar, useTemAcaoDeCriar } from "./acoes-de-criar";
import { useTecladoAberto } from "./use-teclado-aberto";

/** Altura da barra sem a área do gesto — quem fica fixo embaixo (faixa de atendimento, botão da
 *  Agenda, barras de salvar) sobe isso + `env(safe-area-inset-bottom)`. */
export const ALTURA_DA_BARRA = "3.75rem";

/** Pedidos aguardando confirmação — a mesma query do Início do laboratório, então o cache é um só. */
function usePendentesDoLab(ativo: boolean) {
  const q = trpc.prosthesis.incoming.list.useQuery(undefined, { enabled: ativo });
  return useMemo(
    () => ((q.data ?? []) as { status: string }[]).filter((o) => o.status === "AGUARDANDO_CONFIRMACAO").length,
    [q.data],
  );
}

/** A barra de abas do celular. Some com o teclado aberto pra não roubar a tela de quem digita. */
export function BarraDeAbas() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const grupos = useGruposVisiveis();
  const pode = useTemAcaoDeCriar();
  const teclado = useTecladoAberto();
  const [criarAberto, setCriarAberto] = useState(false);
  const [origem, setOrigem] = useState<DOMRect | null>(null);
  const botaoCriar = useRef<HTMLButtonElement>(null);

  const modo = modoDoCelular(user?.activeOrganization);
  const abas = abasDoCelular(modo, grupos.flatMap((g) => g.items), pode.alguma);
  const ativa = abaAtiva(pathname, abas);
  const temPedidos = abas.some((a) => a.chave === "pedidos");
  const pendentes = usePendentesDoLab(temPedidos);

  if (abas.length === 0) return null;

  return (
    <>
      {/* Com o teclado aberto só a barra some — os diálogos do "+" ficam montados (o teclado abre
          justamente quando se digita neles). */}
      <nav
        hidden={teclado}
        aria-label="Navegação principal"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-zinc-200 bg-white md:hidden dark:border-border dark:bg-card"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <ul className="grid" style={{ gridTemplateColumns: `repeat(${abas.length}, minmax(0, 1fr))`, height: ALTURA_DA_BARRA }}>
          {abas.map((aba) => (
            <li key={aba.chave} data-tour={`aba-${aba.chave}`} className="flex">
              {aba.chave === "criar" ? (
                <button
                  ref={botaoCriar}
                  type="button"
                  aria-expanded={criarAberto}
                  onClick={() => {
                    setOrigem(botaoCriar.current?.getBoundingClientRect() ?? null);
                    setCriarAberto(true);
                  }}
                  aria-label="Criar: agendar consulta, cadastrar paciente ou pedir prótese"
                  className="flex flex-1 items-center justify-center"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-600 text-white shadow-sm active:scale-95">
                    <aba.icone className="h-6 w-6" />
                  </span>
                </button>
              ) : (
                <ItemDaBarra aba={aba} ativa={ativa === aba.chave} contador={aba.chave === "pedidos" ? pendentes : 0} />
              )}
            </li>
          ))}
        </ul>
      </nav>
      {pode.alguma && <Criar aberto={criarAberto} onFechar={() => setCriarAberto(false)} origem={origem} />}
    </>
  );
}

function ItemDaBarra({ aba, ativa, contador }: { aba: AbaDoCelular; ativa: boolean; contador: number }) {
  return (
    <Link
      to={aba.href!}
      aria-current={ativa ? "page" : undefined}
      className={cn(
        "flex flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors",
        ativa ? "text-brand-600 dark:text-primary" : "text-zinc-500 dark:text-muted-foreground",
      )}
    >
      <span className="relative">
        <aba.icone className={cn("h-6 w-6", ativa && "stroke-[2.25]")} />
        {contador > 0 && (
          <span
            aria-label={`${contador} aguardando confirmação`}
            className="absolute -right-2.5 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[9.5px] font-bold text-white ring-2 ring-white dark:ring-card"
          >
            {contador > 99 ? "99+" : contador}
          </span>
        )}
      </span>
      <span className="max-w-full truncate px-1">{aba.rotulo}</span>
    </Link>
  );
}

/** Separado da barra pra só montar os diálogos de criar (e as queries deles) quando há o "+". */
function Criar({ aberto, onFechar, origem }: { aberto: boolean; onFechar: () => void; origem: DOMRect | null }) {
  const { acoes, elementos } = useAcoesDeCriar();
  return (
    <>
      <MenuCriar aberto={aberto} onFechar={onFechar} origem={origem} acoes={acoes} />
      {elementos}
    </>
  );
}
